import styled from '@emotion/styled';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import Arranger from '~c/components/base/arranger';
import Button from '~c/components/base/button';
import { DesignToken } from '~c/components/base/core';
import { Ui404 } from '~c/components/base/icon';
import { BrandLogo as BrandLogoDark } from '~c/components/base/icon/asenion-logo-dark';
import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';

interface Props {
  className?: string;
}

const ErrorPageImpl: React.FC<Props> = ({ className }) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className={className}>
      <Arranger direction="column" alignItems="center" justify="center">
        <div className="logo-container">
          <BrandLogoDark />
        </div>

        <Spacer mb={spc.xl} />

        {/* Icon */}
        <div className="error-icon">
          <Ui404 />
        </div>

        <Spacer mb={spc.xs} />

        {/* Main Error Message */}
        <Text sz="subheading-first" className="error-title">
          Page Not Found
        </Text>

        <Spacer mb={spc.s} />

        {/* Description */}
        <Text sz="body16" className="error-description" align="center">
          Sorry, the page you are looking for doesn't exist or has been moved.
        </Text>

        <Spacer mb={spc.xl} />

        {/* Action Buttons */}
        <Arranger direction="row" alignItems="center" className="error-actions">
          <Button onClick={handleGoBack} outlined>
            Go Back
          </Button>
          <Spacer mr={spc.s} />
          <Button onClick={handleGoHome} color="primary">
            Go Home
          </Button>
        </Arranger>
      </Arranger>
    </div>
  );
};

const ErrorPage = styled(ErrorPageImpl)`
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spc.xl * 4}px;
  background-color: ${DesignToken.surface2};

  .logo-container {
    margin-bottom: ${spc.l * 4}px;
    margin-right: ${spc.l * 2}px;

    svg {
      width: 250px;
      height: 100px;
    }
  }

  .error-icon {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background-color: ${DesignToken.status.info}20;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: ${spc.m * 4}px;

    svg {
      width: 60px;
      height: 60px;
      color: ${DesignToken.status.info};
    }
  }

  .error-title {
    color: ${DesignToken.textPrimary};
    font-weight: 600;
    margin: 0;
  }

  .error-description {
    color: ${DesignToken.textMuted};
    max-width: 400px;
    line-height: 1.6;
    margin: 0;
  }

  .error-actions {
    gap: ${spc.s * 4}px;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    padding: ${spc.l * 4}px ${spc.m * 4}px;

    .error-icon {
      width: 80px;
      height: 80px;

      svg {
        width: 40px;
        height: 40px;
      }
    }

    .error-code {
      font-size: 64px;
    }

    .error-actions {
      flex-direction: column;
      width: 100%;
      gap: ${spc.s * 4}px;

      button {
        width: 100%;
        max-width: 200px;
      }
    }
  }
`;

export default ErrorPage;
